import { Component, Input, OnChanges, SimpleChanges, ElementRef, AfterViewInit } from '@angular/core';

import { Dimensions, LegendTooltipPos, LegendTooltipPosEnum, TooltipData, TooltipValue } from '../../models';

import * as d3 from 'd3';
import { gsap } from 'gsap';

class OutsideBoundary
{
    private top2: boolean;
    private top: boolean;
    private right: boolean;
    private right2: boolean;
    private bottom2: boolean;
    private bottom: boolean;
    private left: boolean;
    private left2: boolean;

    /**
     *
     * @param {number} bw Boundary width
     * @param {number} bh Boundary height
     * @param {number} px x-coordinate of the point
     * @param {number} py y-coordinate of the point
     * @param {number} w Tooltip width
     * @param {number} h Tooltip height
     *
     */

    public bestPosition (bw: number, bh: number, px: number, py: number, w: number, h: number, offset: number): LegendTooltipPos
    {
        this.right = (px + offset + w) < bw;
        this.left = (px + offset - w) > 0;
        this.bottom = (py + offset + h) < bh;
        this.top = (py + offset - h) > 0;
        this.right2 = (px + w / 2) < bw;
        this.left2 = (px - w / 2) > 0;
        this.bottom2 = (py + h / 2) < bh;
        this.top2 = (py - h / 2) > 0;

        if (this.right && this.bottom2 && this.top2)
        {
            return LegendTooltipPosEnum.RIGHT;
        }
        else if (this.left && this.bottom2 && this.top2)
        {
            return LegendTooltipPosEnum.LEFT;
        }
        else if (this.top && this.left2 && this.right2)
        {
            return LegendTooltipPosEnum.TOP;
        }

        return LegendTooltipPosEnum.BOTTOM;
    }
}

@Component({
    selector: '[aaTooltip]',
    templateUrl: './tooltip.component.html',
    styleUrls: [
        './tooltip.component.scss'
    ],
    standalone: false
})
export class TooltipComponent implements OnChanges, AfterViewInit
{
    @Input() dimensions: Dimensions;
    @Input() tooltipData: TooltipData;
    @Input() width: number = 180;
    @Input() hovered: boolean = false;
    @Input() guideLines: boolean = true;
    @Input() formatTick: (value: number) => (string) = d3.format('2,.1f');
    public d3: any;
    public posX: number;
    public posY: number;
    public borderPath: string = '';
    public transform: string;
    public radius: number = 4;
    public marginTopHeight: number = 45;
    public titleHeight: number = 20;
    public labelHeight: number = 22;
    public height: number ;
    public offset: number = 7;
    private static duration: number = 0.5;
    private myTooltip: any;
    private myGuideVer: any;
    private myGuideHor: any;
    public tooltipValues: TooltipValue[] = [];

    // Positionnement du SVG
    svgX: number;
    svgY: number;
    public transformItem: string[] = [];

    constructor (
        private elementRef: ElementRef
    )
    {
    }
    ngAfterViewInit (): void
    {
        if (this.guideLines)
        {
            this.myGuideVer = this.elementRef.nativeElement.querySelector('#myGuideVer');
            this.myGuideHor = this.elementRef.nativeElement.querySelector('#myGuideHor');
        }
    }

    ngOnChanges (changes: SimpleChanges)
    {
        let orientation: string = LegendTooltipPosEnum.RIGHT;
        this.myTooltip = this.elementRef.nativeElement.querySelector('#myTooltip');
        if (changes.hovered && changes.hovered.currentValue !== changes.hovered.previousValue)
        {
            this.myTooltip.classList.toggle('unmasked', changes.hovered.currentValue);
            if (this.guideLines && this.myGuideVer && this.myGuideHor)
            {
                this.myGuideVer.classList.toggle('unmasked', changes.hovered.currentValue);
                this.myGuideHor.classList.toggle('unmasked', changes.hovered.currentValue);
            }
        }

        if (this.tooltipData)
        {
            const posX = this.tooltipData.posX;
            const posY = this.tooltipData.posY;
            this.tooltipValues = [...this.tooltipData.values];

            this.height = this.tooltipValues.length * this.labelHeight + this.marginTopHeight;
            this.posX = -1000;
            this.posY = -1000;
            this.borderPath = this.tooltipPath(orientation);
            if (this.guideLines && this.myGuideVer && this.myGuideHor)
            {
                gsap.timeline()
                    .to(this.myGuideHor, { duration: TooltipComponent.duration, attr: { y1: posY, x2: posX, y2: posY }, ease: 'none' }, 0)
                    .to(this.myGuideVer, { duration: TooltipComponent.duration, attr: { x1: posX, x2: posX }, ease: 'none' }, 0)
                    .to(this.myTooltip, { duration: TooltipComponent.duration, css: { x: posX, y: posY }, ease: 'none' }, 0);
            }
            else
            {
                gsap.timeline()
                    .to(this.myTooltip, { duration: TooltipComponent.duration, css: { x: posX, y: posY }, ease: 'none' }, 0);
            }

            orientation = new OutsideBoundary().bestPosition(
                this.dimensions.width,
                this.dimensions.height,
                posX + this.dimensions.marginLeft,
                posY + this.dimensions.marginTop,
                this.width,
                this.height,
                this.offset
            );
            switch (orientation)
            {
                case LegendTooltipPosEnum.TOP:
                    this.svgX = -1 * this.width / 2;
                    this.svgY = -1 * this.offset - this.height;

                    break;
                case LegendTooltipPosEnum.RIGHT:
                    this.svgX = this.offset;
                    this.svgY = -1 * this.height / 2;

                    break;

                case LegendTooltipPosEnum.BOTTOM:
                    this.svgX = -1 * this.width / 2;
                    this.svgY = this.offset;

                    break;

                case LegendTooltipPosEnum.LEFT:
                    this.svgX = -1 * this.width;
                    this.svgY = -1 * this.height / 2;

                    break;
            }
            this.transformItem = this.tooltipValues.map((_, idx: number) =>
                `translate(20px, ${this.labelHeight * idx + this.marginTopHeight}px)`
            );
            this.borderPath = this.tooltipPath(orientation);
        }
    }

    private tooltipPath (position: string): string
    {
        switch (position)
        {
            case 'top':
                return this.topTooltipPath(this.width, this.height, this.offset, this.radius);
            case 'left':
                return this.leftTooltipPath(this.width, this.height, this.offset, this.radius);
            case 'right':
                return this.rightTooltipPath(this.width, this.height, this.offset, this.radius);
            case 'bottom':
                return this.bottomTooltipPath(this.width, this.height, this.offset, this.radius);
        }

        // const method = `${position}TooltipPath`;
        // return this[method](this.width, this.height, this.offset, this.radius);
    }

    private topTooltipPath (width: number, height: number, offset: number, radius: number): string
    {
        const left = -width / 2;
        const right = width / 2;
        const top = -offset - height;
        const bottom = -offset;

        return `
            M 0,0
            L ${-offset},${bottom}
            H ${left + radius}
            Q ${left},${bottom} ${left},${bottom - radius}
            V ${top + radius}
            Q ${left},${top} ${left + radius},${top}
            H ${right - radius}
            Q ${right},${top} ${right},${top + radius}
            V ${bottom - radius}
            Q ${right},${bottom} ${right - radius},${bottom}
            H ${offset}
            L 0,0 z
        `;
    }

    private bottomTooltipPath (width: number, height: number, offset: number, radius: number): string
    {
        const left = -width / 2;
        const right = width / 2;
        const top = offset;
        const bottom = offset + height;

        return `
            M 0,0
            L ${-offset},${top}
            H ${left + radius}
            Q ${left},${top} ${left},${top + radius}
            V ${bottom - radius}
            Q ${left},${bottom} ${left + radius},${bottom}
            H ${right - radius}
            Q ${right},${bottom} ${right},${bottom - radius}
            V ${top + radius}
            Q ${right},${top} ${right - radius},${top}
            H ${offset}
            L 0,0 z
      `;
    }

    private leftTooltipPath (width: number, height: number, offset: number, radius: number): string
    {
        const left = -offset - width;
        const right = -offset;
        const top = -height / 2;
        const bottom = height / 2;

        return `
            M 0,0
            L ${right},${-offset}
            V ${top + radius}
            Q ${right},${top} ${right - radius},${top}
            H ${left + radius}
            Q ${left},${top} ${left},${top + radius}
            V ${bottom - radius}
            Q ${left},${bottom} ${left + radius},${bottom}
            H ${right - radius}
            Q ${right},${bottom} ${right},${bottom - radius}
            V ${offset}
            L 0,0 z
      `;
    }

    private rightTooltipPath (width: number, height: number, offset: number, radius: number): string
    {
        const left = offset;
        const right = offset + width;
        const top = -height / 2;
        const bottom = height / 2;

        return `
            M 0,0
            L ${left},${-offset}
            V ${top + radius}
            Q ${left},${top} ${left + radius},${top}
            H ${right - radius}
            Q ${right},${top} ${right},${top + radius}
            V ${bottom - radius}
            Q ${right},${bottom} ${right - radius},${bottom}
            H ${left + radius}
            Q ${left},${bottom} ${left},${bottom - radius}
            V ${offset}
            L 0,0 z
      `;
    }
}
